import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../../store/store';

export type SummaryRunStatus = 'started' | 'streaming' | 'completed' | 'error';

export type SummarySelection = {
  artifactId: string;
  revision: number;
  source: 'artifact_meta' | 'dropdown' | 'persisted' | 'unknown';
  requestedAt: number;
};

export type SummaryActiveRun = {
  stableId: string;
  status: SummaryRunStatus;
  streamingMarkdown: string;
  artifactId: string | null;
  revision: number | null;
  title: string | null;
  startedAt: number;
  updatedAt: number;
  error: string | null;
};

type SummaryUiConvState = {
  selected: SummarySelection | null;
  activeRun: SummaryActiveRun | null;
  isEditing: boolean;
  isHydratingReplay: boolean;
};

export type SummaryUiState = {
  byConvId: Record<string, SummaryUiConvState>;
};

const initialState: SummaryUiState = { byConvId: {} };

function ensureConvState(state: SummaryUiState, convId: string): SummaryUiConvState {
  const existing = state.byConvId[convId];
  if (existing) return existing;
  const created: SummaryUiConvState = {
    selected: null,
    activeRun: null,
    isEditing: false,
    isHydratingReplay: false,
  };
  state.byConvId[convId] = created;
  return created;
}

function isBlank(val: string): boolean {
  return val.trim() === '';
}

export const summaryUiSlice = createSlice({
  name: 'summaryUi',
  initialState,
  reducers: {
    setEditing(state, action: PayloadAction<{ convId: string; isEditing: boolean }>) {
      const { convId, isEditing } = action.payload;
      ensureConvState(state, convId).isEditing = isEditing;
    },

    setHydrationReplay(
      state,
      action: PayloadAction<{ convId: string; isHydratingReplay: boolean }>
    ) {
      const { convId, isHydratingReplay } = action.payload;
      ensureConvState(state, convId).isHydratingReplay = isHydratingReplay;
    },

    setSelected(
      state,
      action: PayloadAction<{
        convId: string;
        selection: Omit<SummarySelection, 'requestedAt'> & { requestedAt?: number };
      }>
    ) {
      const { convId, selection } = action.payload;
      const conv = ensureConvState(state, convId);
      conv.selected = {
        artifactId: selection.artifactId,
        revision: selection.revision,
        source: selection.source ?? 'unknown',
        requestedAt: selection.requestedAt ?? Date.now(),
      };
    },

    startRun(state, action: PayloadAction<{ convId: string; stableId: string; now: number }>) {
      const { convId, stableId, now } = action.payload;
      const conv = ensureConvState(state, convId);
      conv.activeRun = {
        stableId,
        status: 'started',
        streamingMarkdown: '',
        artifactId: null,
        revision: null,
        title: null,
        startedAt: now,
        updatedAt: now,
        error: null,
      };
    },

    appendRunDelta(
      state,
      action: PayloadAction<{ convId: string; stableId: string; delta: string; now: number }>
    ) {
      const { convId, stableId, delta, now } = action.payload;
      const conv = ensureConvState(state, convId);

      const existing = conv.activeRun;
      if (!existing || existing.stableId !== stableId) {
        conv.activeRun = {
          stableId,
          status: isBlank(delta) ? 'started' : 'streaming',
          streamingMarkdown: delta,
          artifactId: null,
          revision: null,
          title: null,
          startedAt: now,
          updatedAt: now,
          error: null,
        };
        return;
      }

      existing.streamingMarkdown += delta;
      existing.updatedAt = now;
      if (existing.status === 'started' && !isBlank(existing.streamingMarkdown)) {
        existing.status = 'streaming';
      }
    },

    completeRun(
      state,
      action: PayloadAction<{
        convId: string;
        stableId: string;
        success: boolean;
        error?: string;
        now: number;
      }>
    ) {
      const { convId, stableId, success, error, now } = action.payload;
      const conv = ensureConvState(state, convId);
      const existing = conv.activeRun;
      if (!existing || existing.stableId !== stableId) return;

      existing.status = success ? 'completed' : 'error';
      existing.updatedAt = now;
      existing.error = !success ? (error ?? 'unknown error') : null;
    },

    persistRun(
      state,
      action: PayloadAction<{
        convId: string;
        stableId: string;
        artifactId: string;
        revision: number | null;
        title?: string;
        now: number;
      }>
    ) {
      const { convId, stableId, artifactId, revision, title, now } = action.payload;
      const conv = ensureConvState(state, convId);
      const existing = conv.activeRun;
      if (existing && existing.stableId === stableId) {
        existing.artifactId = artifactId;
        existing.revision = revision;
        existing.title = title?.trim() ? title.trim() : existing.title;
        existing.updatedAt = now;
      } else if (existing && !existing.artifactId) {
        // Allow persisted artifact linkage when the streaming source uses
        // a different temporary stableId (e.g., client-side artifact tag projection).
        existing.artifactId = artifactId;
        existing.revision = revision;
        existing.title = title?.trim() ? title.trim() : existing.title;
        existing.updatedAt = now;
      }

      if (
        !conv.isEditing &&
        !conv.isHydratingReplay &&
        typeof revision === 'number' &&
        revision > 0
      ) {
        conv.selected = {
          artifactId,
          revision,
          source: 'persisted',
          requestedAt: now,
        };
      }
    },

    migrateConversation(state, action: PayloadAction<{ fromConvId: string; toConvId: string }>) {
      const { fromConvId, toConvId } = action.payload;
      if (!fromConvId || !toConvId || fromConvId === toConvId) {
        return;
      }

      const fromConv = state.byConvId[fromConvId];
      if (!fromConv) {
        return;
      }

      const toConv = ensureConvState(state, toConvId);
      if (!toConv.selected && fromConv.selected) {
        toConv.selected = fromConv.selected;
      }
      if (!toConv.activeRun && fromConv.activeRun) {
        toConv.activeRun = fromConv.activeRun;
      }
      if (!toConv.isEditing && fromConv.isEditing) {
        toConv.isEditing = true;
      }
      if (!toConv.isHydratingReplay && fromConv.isHydratingReplay) {
        toConv.isHydratingReplay = true;
      }

      delete state.byConvId[fromConvId];
    },

    clearActiveRun(state, action: PayloadAction<{ convId: string; stableId?: string }>) {
      const { convId, stableId } = action.payload;
      const conv = ensureConvState(state, convId);
      if (!stableId || conv.activeRun?.stableId === stableId) {
        conv.activeRun = null;
      }
    },
  },
});

export const {
  appendRunDelta,
  clearActiveRun,
  completeRun,
  migrateConversation,
  persistRun,
  setHydrationReplay,
  setEditing,
  setSelected,
  startRun,
} = summaryUiSlice.actions;

export default summaryUiSlice.reducer;

export const selectSummaryUiConvState = (convId?: string | null) => (state: RootState) => {
  if (!convId) return null;
  return state.summaryUi?.byConvId?.[convId] ?? null;
};

export const selectSummaryActiveRun = (convId?: string | null) => (state: RootState) => {
  if (!convId) return null;
  return state.summaryUi?.byConvId?.[convId]?.activeRun ?? null;
};

export const selectSummarySelection = (convId?: string | null) => (state: RootState) => {
  if (!convId) return null;
  return state.summaryUi?.byConvId?.[convId]?.selected ?? null;
};

export const selectIsSummaryStreaming = (convId?: string | null) => (state: RootState) => {
  if (!convId) return false;
  const run = state.summaryUi?.byConvId?.[convId]?.activeRun;
  if (!run) return false;
  return run.status === 'started' || run.status === 'streaming';
};

export const selectIsHydratingSummaryReplay = (convId?: string | null) => (state: RootState) => {
  if (!convId) return false;
  return state.summaryUi?.byConvId?.[convId]?.isHydratingReplay ?? false;
};
